import React from 'react'
import useCart from '@/public/hooks/use-cart'
import { accessToken } from '@/shared/helpers/CSRF'
import axios from '../../../lib/httpClient'

// What the cart changed about its labs. The server keeps the notices on the
// cart (`cart.lab_company_notices`) until the shopper dismisses them, so both
// the cart drawer and the checkout cart pane show the identical copy off one
// source, however the cart was changed.
//
// Two kinds, told apart by `kind`:
// - none: a lab only available through an employer's plan. Each names the
//   test, the lab company it came from and, when the same test was found
//   elsewhere, the lab company it moved to.
// - 'already_included' (Cart::ALREADY_INCLUDED_NOTICE): a paid lab removed
//   because the plan in the cart already includes it. The plan is named off the
//   cart's own membership line, so the copy follows a swapped plan.
//
// A kind this banner does not know renders nothing, rather than falling into
// the employer copy with its fields missing.
const ALREADY_INCLUDED = 'already_included'

const noticeText = ({
  kind, test, from, to,
}, membership) => {
  if (kind === ALREADY_INCLUDED) {
    return `${test} was removed. It is already included in your ${membership ? `${membership} ` : ''}membership.`
  }
  if (kind) {
    return null
  }

  return to
    ? `${test} is now from ${to} instead of ${from}.`
    : `${test} was removed. It is only available from ${from} through an employer's plan.`
}

export default function LabCompanyNoticeBanner({ cart }) {
  const { mutate } = useCart()
  const membership = cart?.contents?.find(({ data }) => data?.category === 'membership')?.data?.name
  const notices = (cart?.lab_company_notices || [])
    .map((notice) => ({ ...notice, text: noticeText(notice, membership) }))
    .filter(({ text }) => text)
  const employerOnly = notices.some(({ kind }) => !kind)

  if (notices.length === 0) {
    return null
  }

  // Refetched whatever the request's outcome, so a failed dismiss leaves the
  // banner showing what the server still holds rather than a button that did
  // nothing.
  const dismiss = async () => {
    try {
      await axios({
        method: 'delete',
        url: `/api/cart/${cart.uuid}/lab_company_notices`,
        headers: { 'X-CSRF-Token': accessToken },
      })
    } catch {
      // The refetch below shows the notices the server still holds.
    } finally {
      await mutate()
    }
  }

  return (
    <div className="alert alert-info mx-3 mb-3" role="status" aria-live="polite">
      <div className="d-flex align-items-start">
        <div className="flex-grow-1">
          <p className="mb-1">
            We updated your cart.
            {employerOnly && ' Labs from some companies are only available through an employer\'s plan.'}
          </p>
          <ul className="mb-0 ps-3">
            {notices.map((notice) => (
              <li key={`${notice.kind}-${notice.test}-${notice.from}-${notice.product_id}`}>{notice.text}</li>
            ))}
          </ul>
        </div>
        <button type="button" className="btn-close ms-2" aria-label="Dismiss cart changes notice" onClick={dismiss} />
      </div>
    </div>
  )
}
